/** Keto's HTMLAudioElement media route, with optional silent frame observation.
 * Neither a play acknowledgment nor decoded signal is physical speaker proof.
 */
export class VoiceSpeaker {
  constructor({ Context = window.AudioContext, AudioElement = window.Audio, frame = window.requestAnimationFrame.bind(window),
    cancelFrame = window.cancelAnimationFrame.bind(window), now = () => performance.now(), onState = () => {} } = {}) {
    Object.assign(this, { frame, cancelFrame, now, onState });
    this.closed = false; this.current = null; this.context = null;
    this.audio = new AudioElement(); this.audio.muted = true;
    this.audio.setAttribute('playsinline', 'true');
    try {
      this.context = new Context();
      this.gain = this.context.createGain();
      this.gain.gain.setValueAtTime(0, this.context.currentTime);
      this.gain.connect(this.context.destination); // Observation is never an audible second route.
      Promise.resolve(this.context.resume()).catch(() => this.disableObservation());
    } catch (_) { this.disableObservation(); }
  }

  notify(kind, playbackId, basis = null) {
    try { Promise.resolve(this.onState({ kind, playbackId, clientTime: this.now(), basis })).catch(() => {}); }
    catch (_) { /* Optional observation. */ }
  }

  disableObservation() {
    for (const node of [this.source, this.analyser, this.gain]) {
      try { node?.disconnect(); } catch (_) { /* Cleanup continues. */ }
    }
    const context = this.context; this.context = null;
    this.source = null; this.analyser = null; this.gain = null;
    try { Promise.resolve(context?.close()).catch(() => {}); } catch (_) { /* Already closed. */ }
    if (!this.closed) this.notify('observation_unavailable', this.current?.id, 'html_play_ack');
  }

  attach(stream) {
    if (this.closed) return;
    this.audio.srcObject = stream; // Primary output does not depend on optional WebAudio.
    if (!this.context) return;
    try {
      this.source?.disconnect();
      this.source = this.context.createMediaStreamSource(stream);
      this.analyser = this.context.createAnalyser(); this.analyser.fftSize = 2048;
      this.samples = new Float32Array(this.analyser.fftSize);
      this.source.connect(this.analyser); this.analyser.connect(this.gain);
    } catch (_) { this.disableObservation(); }
  }

  async speak(playbackId) {
    if (this.closed) return false;
    this.mute();
    const current = { id: playbackId, started: false, startedAt: null, stoppedAt: null, quietSince: null };
    this.current = current;
    try {
      this.audio.muted = false; await this.audio.play();
      if (this.closed || this.current !== current) return false;
      if (current.stoppedAt !== null) {
        this.mute(); this.notify('unavailable', playbackId, 'html_ack_after_server_drain'); return false;
      }
      this.monitor(current);
      return true;
    } catch (_) {
      if (!this.closed && this.current === current) { this.mute(); this.notify('unavailable', playbackId, 'html_play_rejected'); }
      return false;
    }
  }

  monitor(current) {
    if (this.closed || this.current !== current) return;
    let signal = null;
    if (this.analyser && this.context?.state === 'running' && !this.audio.paused && !this.audio.muted) {
      try {
        this.analyser.getFloatTimeDomainData(this.samples);
        const rms = Math.sqrt(this.samples.reduce((sum, sample) => sum+sample*sample, 0)/this.samples.length);
        signal = rms > 0.001;
      } catch (_) { this.disableObservation(); }
    }
    const now = this.now();
    const playing = !this.audio.paused && !this.audio.muted;
    const basis = signal === null ? 'html_play_ack' : 'decoded_audio_signal';
    if (playing && (signal === true || signal === null)) {
      current.quietSince = null;
      if (!current.started) { current.started = true; current.startedAt = now; this.notify('started', current.id, basis); }
      if (current.lastProgress === undefined || now-current.lastProgress >= 1000) {
        current.lastProgress = now; this.notify('progress', current.id, basis);
      }
    } else if (current.quietSince === null) current.quietSince = now;
    if (current.stoppedAt !== null) {
      if (!playing) { this.mute(); this.notify('unavailable', current.id); return; }
      const drained = signal === null || (current.quietSince !== null && now-current.quietSince >= 200);
      const elapsed = now-current.stoppedAt;
      const firstStart = current.startedAt === null ? current.stoppedAt : current.startedAt;
      // A late decoded start gets its own short tail, within an absolute bound.
      const deadline = Math.min(current.stoppedAt+3000, Math.max(current.stoppedAt, firstStart)+1500);
      if (!current.started && signal !== null && playing) {
        // Short audio or throttled frames can pass between analyser samples.
        // Leave the primary route playing through the full jitter allowance;
        // then release it without inventing a decoded start or heard finish.
        if (elapsed >= 1500) {
          this.mute(); this.notify('drained', current.id, 'provider_buffer_drained'); return;
        }
      } else if (drained || now >= deadline) {
        this.mute();
        this.notify(drained && current.started ? 'finished' : 'unavailable', current.id,
          signal === null ? 'provider_buffer_drained' : 'decoded_audio_signal');
        return;
      }
    }
    this.frameId = this.frame(() => this.monitor(current));
  }

  serverStopped(playbackId) {
    if (this.current?.id === playbackId && this.current.stoppedAt === null) this.current.stoppedAt = this.now();
  }

  mute() {
    try { this.audio.muted = true; this.audio.pause(); } catch (_) { /* Cleanup continues. */ }
    try { if (this.frameId !== undefined) this.cancelFrame(this.frameId); } catch (_) { /* Cleanup continues. */ }
    this.frameId = undefined; this.current = null;
  }

  close() {
    if (this.closed) return;
    this.closed = true; this.mute();
    try { this.audio.srcObject = null; } catch (_) { /* Already detached. */ }
    this.disableObservation();
  }
}
